// apps-kb-modal.js - Modal zone handling for folders

import state from './apps-state.js';
import * as folder from './apps-folders.js';
import { keyboardState } from './apps-kb-state.js';
import * as helpers from './apps-kb-helpers.js';
import * as cursor from './apps-kb-cursor.js';

export function handleModalZoneMovement(direction) {
    const position = keyboardState.virtualCursor.modalZonePosition;
    console.log('handleModalZoneMovement called, direction:', direction, 'position:', position);
    
    if (direction === 'down' && position === 'bottom') {
        console.log('Moving down from bottom zone - exiting to folder');
        // Moving further down from bottom exits back into folder at bottom rank
        const layout = helpers.getLayout();
        const isFolder = !!state.openFolder;
        const icons = helpers.getCurrentIcons();
        const inMoveMode = keyboardState.moveMode.active;
        
        let maxRank;
        if (isFolder && inMoveMode) {
            // In move mode within folder, respect accessible ranks
            const accessibleRanks = helpers.getAccessibleRanksInFolder(inMoveMode);
            maxRank = accessibleRanks - 1; // Convert count to max index
        } else if (isFolder) {
            // In normal mode within folder
            const folderIcons = window.icons.filter(icon => icon.kfolder === state.openFolder.id);
            maxRank = helpers.getMaxOccupiedRank(folderIcons, layout, isFolder);
        } else {
            maxRank = helpers.getMaxOccupiedRank(icons, layout, isFolder);
        }
        
        keyboardState.virtualCursor.isInModalZone = false;
        keyboardState.virtualCursor.modalZonePosition = null;
        console.log('Unhighlighting bottom zone');
        unhighlightModalZone('bottom');
        
        const file = keyboardState.virtualCursor.file;
        cursor.showVirtualCursor(maxRank, file);
        
        // Update move mode indicators if in move mode
        if (inMoveMode) {
            import('./apps-kb-move-mode.js').then(moveMode => {
                moveMode.updateKeyboardDragIndicators(maxRank, file);
            });
        }
    } else if (direction === 'up' && position === 'bottom') {
        console.log('Moving up from bottom zone - going to top of folder');
        // Moving up from bottom zone goes back into folder
        const layout = helpers.getLayout();
        const isFolder = !!state.openFolder;
        const icons = helpers.getCurrentIcons();
        const inMoveMode = keyboardState.moveMode.active;
        
        let maxRank;
        if (isFolder && inMoveMode) {
            // In move mode within folder, respect accessible rnaks
            const accessibleRanks = helpers.getAccessibleRanksInFolder(inMoveMode);
            maxRank = accessibleRanks - 1; // Convert count to max index
        } else if (isFolder) {
            // In normal mode within folder
            const folderIcons = window.icons.filter(icon => icon.kfolder === state.openFolder.id);
            maxRank = helpers.getMaxOccupiedRank(folderIcons, layout, isFolder);
        } else {
            maxRank = helpers.getMaxOccupiedRank(icons, layout, isFolder);
        }
        
        keyboardState.virtualCursor.isInModalZone = false;
        keyboardState.virtualCursor.modalZonePosition = null;
        console.log('Unhighlighting bottom zone');
        unhighlightModalZone('bottom');
        
        const file = keyboardState.virtualCursor.file;
        cursor.showVirtualCursor(maxRank, file);
        
        // Update move mode indicators if in move mode
        if (inMoveMode) {
            import('./apps-kb-move-mode.js').then(moveMode => {
                moveMode.updateKeyboardDragIndicators(maxRank, file);
            });
        }
    } else if (direction === 'up' && position === 'top') {
        console.log('Moving up from top zone - exiting to folder');
        // Moving further up from top exits back into folder at top rank
        const layout = helpers.getLayout();
        const isFolder = !!state.openFolder;
        const icons = helpers.getCurrentIcons();
        const inMoveMode = keyboardState.moveMode.active;
        
        const minRank = inMoveMode ? 0 : helpers.getMinOccupiedRank(icons, layout, isFolder);
        
        keyboardState.virtualCursor.isInModalZone = false;
        keyboardState.virtualCursor.modalZonePosition = null;
        console.log('Unhighlighting top zone');
        unhighlightModalZone('top');
        
        const file = keyboardState.virtualCursor.file;
        cursor.showVirtualCursor(minRank >= 0 ? minRank : 0, file);
        
        // Update move mode indicators if in move mode
        if (inMoveMode) {
            import('./apps-kb-move-mode.js').then(moveMode => {
                moveMode.updateKeyboardDragIndicators(minRank >= 0 ? minRank : 0, file);
            });
        }
    } else if (direction === 'down' && position === 'top') {
        console.log('Moving down from top zone');
        const layout = helpers.getLayout();
        const isFolder = !!state.openFolder;
        const icons = helpers.getCurrentIcons();
        const inMoveMode = keyboardState.moveMode.active;
        
        keyboardState.virtualCursor.isInModalZone = false;
        keyboardState.virtualCursor.modalZonePosition = null;
        console.log('Unhighlighting top zone');
        unhighlightModalZone('top');
        
        // Preserve the file position
        const file = keyboardState.virtualCursor.file;
        
        if (!inMoveMode && isFolder) {
            // Browse mode: go to folder title (but keep file in state)
            console.log('Going to folder title');
            keyboardState.virtualCursor.rank = -0.5;
            keyboardState.virtualCursor.file = file; // Preserve file
            
            const folderTitle = document.getElementById('folderTitle');
            if (folderTitle) {
                folderTitle.focus();
            }
        } else {
            // Move mode: go into folder grid
            console.log('Going to folder grid');
            const minRank = inMoveMode ? 0 : helpers.getMinOccupiedRank(icons, layout, isFolder);
            
            cursor.showVirtualCursor(minRank >= 0 ? minRank : 0, file);
            
            // Update move mode indicators if in move mode
            if (inMoveMode) {
                import('./apps-kb-move-mode.js').then(moveMode => {
                    moveMode.updateKeyboardDragIndicators(minRank >= 0 ? minRank : 0, file);
                });
            }
        }
    } else if (direction === 'left' || direction === 'right') {
        console.log('Moving left/right from zone');
        // Moving left/right exits modal zone
        keyboardState.virtualCursor.isInModalZone = false;
        const exitingPosition = keyboardState.virtualCursor.modalZonePosition;
        keyboardState.virtualCursor.modalZonePosition = null;
        console.log('Unhighlighting zone:', exitingPosition);
        unhighlightModalZone(exitingPosition);
        
        // Return to last valid cursor position in folder
        const layout = helpers.getLayout();
        const isFolder = !!state.openFolder;
        const icons = helpers.getCurrentIcons();
        const inMoveMode = keyboardState.moveMode.active;
        
        // Get a valid rank to return to
        let returnRank;
        if (exitingPosition === 'top') {
            returnRank = inMoveMode ? 0 : helpers.getMinOccupiedRank(icons, layout, isFolder);
            returnRank = returnRank >= 0 ? returnRank : 0;
        } else {
            // Exiting from bottom zone
            if (isFolder && inMoveMode) {
                // In move mode within folder, respect accessible ranks
                const accessibleRanks = helpers.getAccessibleRanksInFolder(inMoveMode);
                returnRank = accessibleRanks - 1; // Convert count to max index
            } else if (isFolder) {
                // In normal mode within folder
                const folderIcons = window.icons.filter(icon => icon.kfolder === state.openFolder.id);
                returnRank = helpers.getMaxOccupiedRank(folderIcons, layout, isFolder);
            } else {
                returnRank = helpers.getMaxOccupiedRank(icons, layout, isFolder);
            }
        }
        
        const file = keyboardState.virtualCursor.file;
        cursor.showVirtualCursor(returnRank, file);
        
        // Update move mode indicators if in move mode
        if (inMoveMode) {
            import('./apps-kb-move-mode.js').then(moveMode => {
                moveMode.updateKeyboardDragIndicators(returnRank, file);
            });
        }
    }
}

export function highlightModalZone(position) {
    const overlay = document.querySelector('.folder-overlay');
    if (!overlay) return;
    
    // Get folder wrapper to calculate zone boundaries
    const folderWrapper = overlay.querySelector('.folder-wrapper');
    if (!folderWrapper) return;
    
    const wrapperRect = folderWrapper.getBoundingClientRect();
    
    // Get folder title to calculate spacing
    const folderTitle = overlay.querySelector('.folder-title');
    const titleRect = folderTitle ? folderTitle.getBoundingClientRect() : null;
    
    // Calculate spacing between title and container (default to 12px if can't determine)
    let spacing = 12;
    if (titleRect) {
        const folderContainer = overlay.querySelector('.folder-container');
        if (folderContainer) {
            const containerRect = folderContainer.getBoundingClientRect();
            spacing = containerRect.top - titleRect.bottom;
        }
    }
    
    // Create backdrop zones if they don't exist
    let topBackdrop = overlay.querySelector('.folder-backdrop-top');
    let bottomBackdrop = overlay.querySelector('.folder-backdrop-bottom');
    
    if (!topBackdrop) {
        topBackdrop = document.createElement('div');
        topBackdrop.className = 'folder-backdrop-top';
        overlay.appendChild(topBackdrop);
    }
    
    if (!bottomBackdrop) {
        bottomBackdrop = document.createElement('div');
        bottomBackdrop.className = 'folder-backdrop-bottom';
        overlay.appendChild(bottomBackdrop);
    }
    
    // Position top backdrop from screen top to just above folder title
    topBackdrop.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        height: ${wrapperRect.top}px;
        background: transparent;
        z-index: 99;
        pointer-events: none;
        transition: background 0.2s ease;
    `;
    
    // Position bottom backdrop from below folder container (with spacing) to screen bottom
    bottomBackdrop.style.cssText = `
        position: fixed;
        top: ${wrapperRect.bottom + spacing}px;
        left: 0;
        right: 0;
        bottom: 0;
        background: transparent;
        z-index: 99;
        pointer-events: none;
        transition: background 0.2s ease;
    `;
    
    const zone = position === 'top' ? topBackdrop : bottomBackdrop;
    
    if (!zone) return;
    
    // Highlight zone with blue color like scroll zones
    zone.style.background = 'rgba(74, 144, 226, 0.5)';
    
    // Hide any drop indicators when in modal zone
    const dropIndicators = document.querySelectorAll('.insert-indicator, #folderInsertIndicator');
    dropIndicators.forEach(indicator => {
        if (indicator) {
            indicator.style.display = 'none';
        }
    });
    
    // Show arrow cursor in the modal zone at the current file position
    const layout = helpers.getLayout();
    const file = keyboardState.virtualCursor.file;
    
    // Get the folder container to calculate dimensions
    const container = document.getElementById('folderScreens');
    if (!container) return;
    
    // Get any screen element to calculate icon sizing
    const screenElement = container.querySelector('.folder-screen');
    if (!screenElement) return;
    
    // Find an actual icon slot to get accurate positioning
    const currentScreen = helpers.getCurrentScreenForRegions();
    const regionsPerScreen = layout.leaves;
    const screenStartRegion = currentScreen * regionsPerScreen;
    
    // Calculate which slot we want
    const regionLeaf = Math.floor(file / 4);
    const posFile = file % 4;
    const slotRegion = screenStartRegion + regionLeaf;
    const slotPosition = posFile; // Position 0-11, but we only care about file (0-3)
    
    // Try to find a slot in this file to get exact positioning
    let referenceSlot = null;
    for (let rank = 0; rank < 3; rank++) {
        const testPosition = rank * 4 + posFile;
        const slot = container.querySelector(
            `.icon-slot[data-region="${slotRegion}"][data-position="${testPosition}"]`
        );
        if (slot) {
            referenceSlot = slot;
            break;
        }
    }
    
    let clientX;
    if (referenceSlot) {
        // Use actual slot position
        const slotRect = referenceSlot.getBoundingClientRect();
        clientX = slotRect.left + slotRect.width / 2;
    } else {
        // Fallback: calculate position based on CSS variables
        const computedIconSize = parseInt(
            getComputedStyle(document.documentElement).getPropertyValue('--icon-size')
        ) || 100;
        const iconGapH = parseInt(
            getComputedStyle(document.documentElement).getPropertyValue('--icon-gap-horizontal')
        ) || 20;
        const regionGapH = parseInt(
            getComputedStyle(document.documentElement).getPropertyValue('--region-gap-h')
        ) || 40;
        
        const screenRect = screenElement.getBoundingClientRect();
        const regionWidth = (computedIconSize * 4) + (iconGapH * 3);
        
        const xOffsetFromScreenLeft = (regionLeaf * (regionWidth + regionGapH)) + 
                                       (posFile * (computedIconSize + iconGapH)) + 
                                       (computedIconSize / 2);
        
        clientX = screenRect.left + xOffsetFromScreenLeft;
        console.log('Using calculated X position:', clientX);
    }
    
    // Get zone rect for Y position (center of the zone)
    const zoneRect = zone.getBoundingClientRect();
    const clientY = zoneRect.top + zoneRect.height / 2;
    
    console.log('Arrow position - clientX:', clientX, 'clientY:', clientY);
    
    // Create arrow cursor for modal zone
    const arrow = document.createElement('div');
    arrow.id = 'virtualCursorArrow';
    arrow.style.cssText = `
        position: fixed;
        left: ${clientX}px;
        top: ${clientY}px;
        transform: translate(0, 0);
        pointer-events: none;
        z-index: 10001;
        filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.4));
    `;
    
    arrow.innerHTML = `
        <svg width="40" height="40" viewBox="0 0 24 24" style="display: block;">
            <defs>
                <linearGradient id="arrowGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" style="stop-color:#4a90e2;stop-opacity:1" />
                    <stop offset="100%" style="stop-color:#667eea;stop-opacity:1" />
                </linearGradient>
            </defs>
            <path d="M 3,3 3.1630606,18.467545 7.57322,14.141424 11.510818,21 l 2.652243,-1.529947 -3.856068,-6.532455 6.103166,-0.815303 z"
                  fill="url(#arrowGradient)" 
                  stroke="white" 
                  stroke-width="1.5"
                  stroke-linejoin="round"/>
        </svg>
    `;
    
    document.body.appendChild(arrow);
    
    // Also show the ghost icon if in move mode
    if (keyboardState.moveMode.active && state.dragGhost) {
        console.log('Updating ghost position in move mode');
        import('./apps-kb-cursor.js').then(cursor => {
            cursor.updateGhostPosition(clientX, clientY);
        });
    }
}

export function unhighlightModalZone(position) {
    console.log('unhighlightModalZone called with position:', position);
    const overlay = document.querySelector('.folder-overlay');
    if (!overlay) return;
    
    const zone = position === 'top' ? 
        overlay.querySelector('.folder-backdrop-top') : 
        overlay.querySelector('.folder-backdrop-bottom');
    
    if (zone) {
        zone.style.background = 'transparent';
    }
    
    // Remove arrow cursor when leaving modal zone
    const arrow = document.getElementById('virtualCursorArrow');
    if (arrow) {
        arrow.remove();
    }
    
    // Restore drop indicators
    const dropIndicators = document.querySelectorAll('.insert-indicator, #folderInsertIndicator');
    dropIndicators.forEach(indicator => {
        if (indicator) {
            indicator.style.display = '';
        }
    });
}

export function clearAllModalZones() {
    // Unhighlight both zones
    unhighlightModalZone('top');
    unhighlightModalZone('bottom');
    
    // Remove backdrop elements completely
    const overlay = document.querySelector('.folder-overlay');
    if (overlay) {
        const topBackdrop = overlay.querySelector('.folder-backdrop-top');
        const bottomBackdrop = overlay.querySelector('.folder-backdrop-bottom');
        
        if (topBackdrop) topBackdrop.remove();
        if (bottomBackdrop) bottomBackdrop.remove();
    }
}

export function handleModalZoneClick() {
    console.log('handleModalZoneClick called, isInModalZone:', keyboardState.virtualCursor.isInModalZone);
    if (!keyboardState.virtualCursor.isInModalZone) return;
    
    // Store move mode state and file before closing folder
    const wasMoveMode = keyboardState.moveMode.active;
    const draggedIcon = keyboardState.moveMode.draggedIcon;
    const currentFile = keyboardState.virtualCursor.file;
    
    // Store folder info before it's cleared
    const folderId = state.openFolder?.id;
    console.log('folderId:', folderId);
    
    // Clear any drop indicators before closing
    import('./apps-drag.js').then(drag => {
        drag.clearDropIndicators();
    });
    
    // Close the folder
    console.log('Closing folder view...');
    folder.closeFolderView();
    
    // After folder closes, handle positioning
    if (wasMoveMode && draggedIcon && folderId) {
        console.log('Handling move mode case...');
        // Move mode: position cursor on folder icon and keep move mode active
        setTimeout(() => {
            const folderIcon = window.icons.find(icon => icon.id === folderId);
            if (folderIcon) {
                const layout = helpers.getLayout();
                const visualPos = helpers.regionPositionToRankFile(folderIcon.region, folderIcon.position, layout, false);
                
                // Navigate to the correct screen if needed
                if (visualPos.screen !== undefined && visualPos.screen !== state.currentScreen) {
                    import('./apps-navigation.js').then(nav => {
                        nav.goToScreen(visualPos.screen);
                    });
                }
                
                // Position cursor on folder icon
                cursor.showVirtualCursor(visualPos.rank, visualPos.file);
                
                // Activate main screen scroll zones since we're in move mode
                document.getElementById('scrollZoneLeft')?.classList.add('active');
                document.getElementById('scrollZoneRight')?.classList.add('active');
                
                // Update ghost icon position to be on top of the folder icon
                if (keyboardState.moveMode.active && state.dragGhost) {
                    const container = document.getElementById('appspaceScreens');
                    if (container) {
                        const currentScreen = helpers.getCurrentScreenForRegions();
                        const regionsPerScreen = layout.leaves * layout.bands;
                        const screenStartRegion = currentScreen * regionsPerScreen;
                        const slotInfo = helpers.rankFileToRegionPosition(visualPos.rank, visualPos.file, layout, false);
                        
                        if (slotInfo) {
                            const actualRegion = screenStartRegion + slotInfo.region;
                            const slotElement = container.querySelector(
                                `.icon-slot[data-region="${actualRegion}"][data-position="${slotInfo.position}"]`
                            );
                            
                            if (slotElement) {
                                const iconElement = slotElement.querySelector('.appicon');
                                const rect = iconElement ? iconElement.getBoundingClientRect() : slotElement.getBoundingClientRect();
                                const clientX = rect.left + rect.width / 2;
                                const clientY = rect.top + rect.height / 2;
                                
                                // Update ghost position
                                cursor.updateGhostPosition(clientX, clientY);
                            }
                        }
                    }
                }
            }
        }, 150);
    } else {
        console.log('Not in move mode or missing data, folder just closes');
    }
    // If not in move mode, just close the folder (already done above)
    // The handleFolderClose in navigation will position the cursor
}
